import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import LegalDocumentModal from "../legal/LegalDocumentModal";
import { Eye, EyeOff, Mail, Lock, User, Building } from "lucide-react";

const LEGAL_VERSION = "2026-05-07";

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [legalModalType, setLegalModalType] = useState(null);
  const { register: registerUser, isLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const inviteCode = searchParams.get("invite") || "";
  const inviteEmail = searchParams.get("email") || "";

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
  } = useForm();

  const password = watch("password");

  const openLegalModal = (type) => {
    setLegalModalType(type);
  };

  const closeLegalModal = () => {
    setLegalModalType(null);
  };

  const onSubmit = async (data) => {
    if (data.password !== data.confirmPassword) {
      setError("confirmPassword", {
        message: t("auth.register.passwordMismatch"),
      });
      return;
    }

    const { confirmPassword, ...userData } = data;
    userData.termsVersion = LEGAL_VERSION;
    userData.privacyVersion = LEGAL_VERSION;
    if (inviteCode) userData.inviteCode = inviteCode;
    const result = await registerUser(userData);

    if (result.success) {
      if (result.requiresVerification) {
        const email = result.email || userData.email;
        const target = email
          ? `/verify-email?email=${encodeURIComponent(email)}`
          : "/verify-email";
        navigate(target, { state: { email } });
      } else {
        navigate("/dashboard");
      }
    } else if (
      result.details &&
      Array.isArray(result.details) &&
      result.details.length > 0
    ) {
      const knownFields = [
        "firstName",
        "lastName",
        "email",
        "password",
        "confirmPassword",
        "organization",
        "termsAccepted",
        "privacyAccepted",
      ];
      let hasFieldError = false;
      result.details.forEach(({ path, msg }) => {
        if (knownFields.includes(path)) {
          setError(path, { message: msg });
          hasFieldError = true;
        }
      });
      if (!hasFieldError) {
        setError("root", {
          message: t("auth.register.errors.validationFailed"),
        });
      }
    } else {
      setError("root", {
        message: result.error || t("auth.register.errors.validationFailed"),
      });
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#020617] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute -top-32 right-[-10%] h-96 w-96 rounded-full bg-cyan-500/30 blur-[120px]" />
        <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-blue-500/20 blur-[120px]" />
        <div className="absolute inset-0 opacity-20">
          <div className="[background:radial-gradient(circle_at_1px,_rgba(255,255,255,0.25)_1px,_transparent_0)] h-full w-full [background-size:40px_40px]" />
        </div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-3xl rounded-3xl border border-white/10 bg-slate-900/70 p-8 shadow-2xl shadow-cyan-500/20 backdrop-blur-xl">
          <div className="text-center">
            <h2 className="text-3xl font-semibold text-white">
              {t("auth.register.title")}
            </h2>
          </div>

          <form className="mt-10 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">
                  {t("auth.register.firstName")}
                </label>
                <div className="relative">
                  <input
                    {...register("firstName", {
                      required: t("auth.register.errors.firstNameRequired"),
                      minLength: {
                        value: 2,
                        message: t("auth.register.errors.firstNameMinLength"),
                      },
                    })}
                    type="text"
                    className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-12 py-3 text-white placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  />
                  <User className="input-icon-left text-slate-400" />
                </div>
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-400">
                    {errors.firstName.message}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">
                  {t("auth.register.lastName")}
                </label>
                <div className="relative">
                  <input
                    {...register("lastName", {
                      required: t("auth.register.errors.lastNameRequired"),
                      minLength: {
                        value: 2,
                        message: t("auth.register.errors.lastNameMinLength"),
                      },
                    })}
                    type="text"
                    className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-12 py-3 text-white placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  />
                  <User className="input-icon-left text-slate-400" />
                </div>
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-400">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">
                  {t("auth.register.email")}
                </label>
                <div className="relative">
                  <input
                    {...register("email", {
                      required: t("auth.register.errors.emailRequired"),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t("auth.register.errors.emailInvalid"),
                      },
                    })}
                    type="email"
                    defaultValue={inviteEmail}
                    readOnly={!!inviteEmail}
                    className={`w-full rounded-2xl border border-white/10 bg-slate-950/40 px-12 py-3 text-white placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-400 ${
                      inviteEmail ? "cursor-not-allowed opacity-70" : ""
                    }`}
                    placeholder="ava.schneider@company.ch"
                  />
                  <Mail className="input-icon-left text-slate-400" />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-400">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">
                  {t("auth.register.organization")}{" "}
                  <span className="text-slate-500">
                    ({t("common.optional")})
                  </span>
                </label>
                <div className="relative">
                  <input
                    {...register("organization")}
                    type="text"
                    className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-12 py-3 text-white placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    placeholder={t("auth.register.placeholders.organization")}
                  />
                  <Building className="input-icon-left text-slate-400" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">
                  {t("auth.register.password")}
                </label>
                <div className="relative">
                  <input
                    {...register("password", {
                      required: t("auth.register.errors.passwordRequired"),
                      minLength: {
                        value: 12,
                        message: t("auth.register.errors.passwordMinLength"),
                      },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                        message: t("auth.register.errors.passwordPattern"),
                      },
                    })}
                    type={showPassword ? "text" : "password"}
                    className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-12 py-3 text-white placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    placeholder="••••••••"
                  />
                  <Lock className="input-icon-left text-slate-400" />
                  <button
                    type="button"
                    className="input-icon-right text-slate-300 hover:text-white"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-400">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">
                  {t("auth.register.confirmPassword")}
                </label>
                <div className="relative">
                  <input
                    {...register("confirmPassword", {
                      required: t(
                        "auth.register.errors.confirmPasswordRequired",
                      ),
                    })}
                    type={showConfirmPassword ? "text" : "password"}
                    className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-12 py-3 text-white placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    placeholder="••••••••"
                  />
                  <Lock className="input-icon-left text-slate-400" />
                  <button
                    type="button"
                    className="input-icon-right text-slate-300 hover:text-white"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-400">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </div>

            {errors.root && (
              <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
                {errors.root.message}
              </div>
            )}

            <div className="space-y-3 rounded-2xl border border-white/10 bg-slate-950/40 p-4 text-sm text-slate-300">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  {...register("termsAccepted", {
                    required: t("auth.register.termsRequired"),
                  })}
                  className="mt-1 h-4 w-4 rounded border-white/20 bg-slate-900 text-cyan-400 focus:ring-cyan-400"
                />
                <span>
                  {t("auth.register.acceptTermsPrefix")}{" "}
                  <button
                    type="button"
                    onClick={() => openLegalModal("terms")}
                    className="font-semibold text-cyan-300 hover:text-cyan-200"
                  >
                    {t("auth.register.termsLink")}
                  </button>
                </span>
              </label>
              {errors.termsAccepted && (
                <p className="text-sm text-red-400">
                  {errors.termsAccepted.message}
                </p>
              )}

              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  {...register("privacyAccepted", {
                    required: t("auth.register.privacyRequired"),
                  })}
                  className="mt-1 h-4 w-4 rounded border-white/20 bg-slate-900 text-cyan-400 focus:ring-cyan-400"
                />
                <span>
                  {t("auth.register.acceptPrivacyPrefix")}{" "}
                  <button
                    type="button"
                    onClick={() => openLegalModal("privacy")}
                    className="font-semibold text-cyan-300 hover:text-cyan-200"
                  >
                    {t("auth.register.privacyLink")}
                  </button>
                </span>
              </label>
              {errors.privacyAccepted && (
                <p className="text-sm text-red-400">
                  {errors.privacyAccepted.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-cyan-500/30 transition hover:shadow-cyan-400/50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              ) : (
                t("auth.register.submit")
              )}
            </button>

            <div className="text-center text-sm text-slate-400">
              {t("auth.register.haveAccount")}{" "}
              <Link
                to="/login"
                className="font-semibold text-white hover:text-cyan-200"
              >
                {t("auth.register.signIn")}
              </Link>
            </div>
          </form>
        </div>
      </div>

      {legalModalType && (
        <LegalDocumentModal type={legalModalType} onClose={closeLegalModal} />
      )}
    </div>
  );
};

export default Register;
